import { ReactElement, ReactNode } from 'react'
import { RefCallBack } from 'react-hook-form'
import { InputLayout, InputThemeStyle } from 'components/atoms/inputs/InputLayout/InputLayout'
import { Input, InputProps } from 'components/atoms/inputs/Input/Input'

type BaseLabeledInputProps = {
  name: string
  inputClassName?: string

  error?: ReactNode
  dynamicHeight?: boolean
  label?: ReactNode
  note?: ReactNode
  units?: ReactNode
  hasFocus?: boolean
  style?: InputThemeStyle
  horizontal?: boolean
  className?: string
  innerRef?: RefCallBack
}

type LabeledInputProps<OptionValue extends string> = BaseLabeledInputProps & InputProps<OptionValue>

/**
 * Master Component to use for all forms unless some extreme exception.
 * This will take an input field and wrap it in a label,
 * as well as all other deocrations such as hints, units and errors.
 *
 * Use inside react-hook-form context or by nesting inside FormPreset
 *
 * @type - InputType - See the Insight.jsx component to see which options are available
 */
export const LabeledInput = <OptionValue extends string> (props: LabeledInputProps<OptionValue>): ReactElement | null => {
  const {
    className,
    dynamicHeight,
    error,
    hasFocus,
    horizontal,
    label,
    note,
    units,
    style,
    inputClassName,
    ...inputProps
  } = props

  const { name, disabled } = inputProps

  const inputHasOptions = props.type === 'select' || props.type === 'radio' || props.type === 'checkboxes'
  const layoutProps = { className, disabled, dynamicHeight, error, hasFocus, label, name, note, units, style, horizontal }

  return (
    <InputLayout
      { ...layoutProps }
      wrapInLabel={ !inputHasOptions }
      textInputMode={ !inputHasOptions }
      themeStyle={ style }
    >
      <Input { ...inputProps } className={ inputClassName } />
    </InputLayout>
  )
}
